import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Extract_PropertyOperation_MeliPublicationDraftOperation_,
  type PrepareMeliPublicationDraftParams,
  PropertyOperation,
  type CatalogProperty,
  type MeliPublicationDraftResponse,
  type PropertyPortalPublication,
  MeliPublicationTarget,
} from 'src/api/generated';
import { useAlert } from 'src/contexts/alert-context';
import {
  useEnqueueMeliPublish,
  usePrepareMeliPublicationDraft,
} from 'src/modules/shared/hooks/api/integrations/meli-publications';
import {
  useEnqueuePortalPublicationPause,
  useEnqueuePortalPublicationUnpublish,
  usePortalPublicationEvents,
} from 'src/modules/shared/hooks/api/integrations/portal-publications';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { extractApiErrorMessage } from 'src/modules/shared/utils/api/error-handler.util';
import {
  canPauseMeliPublication,
  canUnpublishMeliPublication,
  getCurrentMeliPublication,
  getMeliPublicationById,
  getMeliPublicationConfigString,
  isMeliPublicationPaymentRequired,
  isMeliPublicationBillingBlocked,
  type MeliPublicationAction,
  type SupportedMeliOperation,
} from '../utils/meli-publication.util';
import {
  getPublicationAlerts,
  type PropertyPublicationAlert,
} from '../utils/property-publication-alert.util';
import { parsePropertyCoordinate } from '../utils/property-location.util';
import {
  getDraftIssueVariant,
  getMeliListingTypeLabel,
  getPropertyImageFileIds,
  getPublicationPortalTarget,
  isPortalTarget,
} from '../utils/property-meli-publishing-manager.util';
import {
  getMeliDraftIssueFieldLabelKey,
  getMeliDraftIssueMessageKey,
  getMeliDraftIssueMessageParams,
  groupMeliDraftIssuesBySection,
} from '../utils/meli-draft-issues.util';

interface QueuedPublicationAction {
  action: MeliPublicationAction;
  operation: SupportedMeliOperation;
}

interface UsePropertyMeliPublishingManagerParams {
  property: CatalogProperty;
  selectedOperation: SupportedMeliOperation;
  selectedPublicationId?: number | null;
  publications: PropertyPortalPublication[];
  refetchPublications: () => Promise<unknown>;
  setQueuedAction: (value: QueuedPublicationAction | null) => void;
  isSyncMode: boolean;
}

interface MeliPublishingManagerFormState {
  effectiveListingTypeId: string;
  listingTypeId: string | null;
  listingTypeOptions: { value: string; content: string }[];
  portalTarget: MeliPublicationTarget;
  portalTargetOptions: {
    value: MeliPublicationTarget;
    content: string;
  }[];
  selectedListingTypeOption: { value: string; content: string } | null;
  selectedPictureFileIds: string[];
}

interface MeliPublishingManagerDraftState {
  data: MeliPublicationDraftResponse | null;
  groupedIssues: ReturnType<typeof groupMeliDraftIssuesBySection>;
  issueVariant: 'success' | 'warning' | null;
  loading: boolean;
  params: PrepareMeliPublicationDraftParams;
  publishBlockedByIssues: boolean;
}

interface MeliPublishingManagerPublicationState {
  alerts: PropertyPublicationAlert[];
  canPauseCurrentPublication: boolean;
  canUnpublishCurrentPublication: boolean;
  currentPublication: PropertyPortalPublication | null;
  isSyncManagedProperty: boolean;
  paymentRequired: boolean;
  publicationEvents: ReturnType<typeof usePortalPublicationEvents>['events'];
}

interface MeliPublishingManagerSyncState {
  isSyncMode: boolean;
}

interface MeliPublishingManagerLoadingState {
  action: boolean;
  enqueueingPause: boolean;
  enqueueingPublish: boolean;
  enqueueingUnpublish: boolean;
}

interface MeliPublishingManagerActions {
  getDraftIssueFieldLabel: (field: string) => string;
  getDraftIssueMessage: (
    issue: Parameters<typeof getMeliDraftIssueMessageKey>[0]
  ) => string;
  handleEnqueuePublish: () => Promise<void>;
  handlePersistedAction: (
    action: 'pause' | 'unpublish',
    publicationId: number
  ) => Promise<void>;
  handlePortalTargetChange: (value: string) => void;
  setDraft: Dispatch<SetStateAction<MeliPublicationDraftResponse | null>>;
  setListingTypeId: Dispatch<SetStateAction<string | null>>;
}

export interface UsePropertyMeliPublishingManagerResult {
  actions: MeliPublishingManagerActions;
  draft: MeliPublishingManagerDraftState;
  form: MeliPublishingManagerFormState;
  loading: MeliPublishingManagerLoadingState;
  publication: MeliPublishingManagerPublicationState;
  sync: MeliPublishingManagerSyncState;
}

/**
 * State and actions for the inline Mercado Libre publication manager.
 */
export const usePropertyMeliPublishingManager = ({
  property,
  selectedOperation,
  selectedPublicationId = null,
  publications,
  refetchPublications,
  setQueuedAction,
  isSyncMode,
}: UsePropertyMeliPublishingManagerParams): UsePropertyMeliPublishingManagerResult => {
  const { t } = useI18n();
  const { showAlert } = useAlert();
  const [listingTypeId, setListingTypeId] = useState<string | null>(null);
  const [portalTarget, setPortalTarget] =
    useState<MeliPublicationTarget | null>(null);
  const propertyPictureFileIds = useMemo(
    () => getPropertyImageFileIds(property.media),
    [property.media]
  );
  const [draft, setDraft] = useState<MeliPublicationDraftResponse | null>(null);
  const latestDraftRequestIdRef = useRef(0);
  const lastAutoDraftRequestKeyRef = useRef<string | null>(null);

  const currentPublication = useMemo(
    () =>
      getMeliPublicationById(publications, selectedPublicationId) ??
      getCurrentMeliPublication(publications, selectedOperation),
    [publications, selectedOperation, selectedPublicationId]
  );
  const { events: publicationEvents } = usePortalPublicationEvents(
    currentPublication?.id,
    currentPublication != null
  );
  useEffect(() => {
    setListingTypeId(null);
    setPortalTarget(
      currentPublication ? getPublicationPortalTarget(currentPublication) : null
    );
    setDraft(null);
  }, [currentPublication, selectedOperation]);

  const { prepareDraft, loading: preparingDraft } =
    usePrepareMeliPublicationDraft();
  const { enqueuePublish, loading: enqueueingPublish } =
    useEnqueueMeliPublish();
  const { enqueuePersistedAction: enqueuePause, loading: enqueueingPause } =
    useEnqueuePortalPublicationPause();
  const {
    enqueuePersistedAction: enqueueUnpublish,
    loading: enqueueingUnpublish,
  } = useEnqueuePortalPublicationUnpublish();

  const loadingAction =
    enqueueingPublish || enqueueingPause || enqueueingUnpublish;
  const publishBlockedByInactiveProperty = property.isActive !== true;
  const publishBlockedByIssues =
    publishBlockedByInactiveProperty ||
    isMeliPublicationBillingBlocked(currentPublication) ||
    draft == null ||
    draft.issues.length > 0;
  const currentPublicationListingTypeId = useMemo(
    () => getMeliPublicationConfigString(currentPublication, 'listingTypeId'),
    [currentPublication]
  );

  const listingTypeOptions = useMemo(() => {
    const options = [
      {
        value: '',
        content: t('catalog.properties.meli.form.listingType.default'),
      },
      ...(draft?.availableListingTypes.map((item) => ({
        value: item.id,
        content: item.name,
      })) ?? []),
    ];

    if (
      currentPublicationListingTypeId &&
      !options.some(
        (option) => option.value === currentPublicationListingTypeId
      )
    ) {
      options.splice(1, 0, {
        value: currentPublicationListingTypeId,
        content: getMeliListingTypeLabel(currentPublicationListingTypeId, t),
      });
    }

    return options;
  }, [currentPublicationListingTypeId, draft?.availableListingTypes, t]);

  const portalTargetOptions = useMemo(
    () => [
      {
        value: MeliPublicationTarget.MELI,
        content: t('catalog.properties.meli.portalTarget.meli'),
      },
      {
        value: MeliPublicationTarget.MELI_POI,
        content: t('catalog.properties.meli.portalTarget.meliPoi'),
      },
    ],
    [t]
  );

  const groupedDraftIssues = useMemo(
    () => groupMeliDraftIssuesBySection(draft?.issues ?? []),
    [draft?.issues]
  );
  const requestedListingTypeId = useMemo(
    () => listingTypeId ?? currentPublicationListingTypeId ?? null,
    [currentPublicationListingTypeId, listingTypeId]
  );
  const effectiveListingTypeId = useMemo(
    () => requestedListingTypeId ?? draft?.listingTypeId ?? '',
    [draft?.listingTypeId, requestedListingTypeId]
  );
  const selectedListingTypeOption = useMemo(
    () =>
      listingTypeOptions.find(
        (option) => option.value === effectiveListingTypeId
      ) ?? null,
    [effectiveListingTypeId, listingTypeOptions]
  );
  const isSyncManagedProperty = property.sourceSystem === 'meli';
  const paymentRequired = isMeliPublicationPaymentRequired(currentPublication);
  const publicationAlerts = useMemo(
    () => getPublicationAlerts(currentPublication),
    [currentPublication]
  );

  const draftParams = useMemo((): PrepareMeliPublicationDraftParams => {
    const latitude = parsePropertyCoordinate(property.latitude);
    const longitude = parsePropertyCoordinate(property.longitude);

    return {
      propertyId: property.id,
      operation:
        selectedOperation === PropertyOperation.SALE
          ? Extract_PropertyOperation_MeliPublicationDraftOperation_.SALE
          : Extract_PropertyOperation_MeliPublicationDraftOperation_.RENT,
      listingTypeId: requestedListingTypeId?.trim() ?? null,
      portalTarget,
      pictureFileIds: propertyPictureFileIds,
      locationHint:
        latitude != null && longitude != null
          ? {
              latitude,
              longitude,
              locality: property.locality ?? null,
              region: property.region ?? null,
              neighborhood: property.neighborhood ?? null,
              addressLine: property.addressDisplay ?? null,
            }
          : null,
    };
  }, [
    portalTarget,
    property.addressDisplay,
    property.id,
    property.latitude,
    property.locality,
    property.longitude,
    property.neighborhood,
    property.region,
    requestedListingTypeId,
    selectedOperation,
    propertyPictureFileIds,
  ]);

  const prepareDraftPreview = useCallback(
    async (
      silent = false,
      force = false
    ): Promise<MeliPublicationDraftResponse | null> => {
      const requestKey = JSON.stringify(draftParams);
      if (
        !force &&
        silent &&
        lastAutoDraftRequestKeyRef.current === requestKey
      ) {
        return draft;
      }

      const requestId = latestDraftRequestIdRef.current + 1;
      latestDraftRequestIdRef.current = requestId;
      lastAutoDraftRequestKeyRef.current = requestKey;

      try {
        const nextDraft = await prepareDraft(draftParams);
        if (latestDraftRequestIdRef.current !== requestId) {
          return null;
        }

        setDraft(nextDraft);

        return nextDraft;
      } catch (error) {
        if (!silent) {
          showAlert(extractApiErrorMessage(error), 'error');
        }

        return null;
      }
    },
    [draft, draftParams, prepareDraft, showAlert]
  );

  useEffect(() => {
    void prepareDraftPreview(true);
  }, [prepareDraftPreview]);

  const handleEnqueuePublish = useCallback(async () => {
    const nextDraft = await prepareDraftPreview();
    if (!nextDraft || nextDraft.issues.length > 0) {
      showAlert(t('catalog.properties.meli.alert.fixDraftIssues'), 'warning');
      return;
    }

    try {
      await enqueuePublish(draftParams);
      setQueuedAction({ action: 'publish', operation: selectedOperation });
      showAlert(t('catalog.properties.meli.alert.publishQueued'), 'success');
      await refetchPublications();
    } catch (error) {
      showAlert(extractApiErrorMessage(error), 'error');
    }
  }, [
    draftParams,
    enqueuePublish,
    prepareDraftPreview,
    refetchPublications,
    selectedOperation,
    setQueuedAction,
    showAlert,
    t,
  ]);

  const handlePersistedAction = useCallback(
    async (action: 'pause' | 'unpublish', publicationId: number) => {
      try {
        if (action === 'pause') {
          await enqueuePause(publicationId);
        } else {
          await enqueueUnpublish(publicationId);
        }

        setQueuedAction({ action, operation: selectedOperation });
        showAlert(
          t(
            action === 'pause'
              ? 'catalog.properties.meli.alert.pauseQueued'
              : 'catalog.properties.meli.alert.unpublishQueued'
          ),
          'success'
        );
        await refetchPublications();
      } catch (error) {
        showAlert(extractApiErrorMessage(error), 'error');
      }
    },
    [
      enqueuePause,
      enqueueUnpublish,
      refetchPublications,
      selectedOperation,
      setQueuedAction,
      showAlert,
      t,
    ]
  );

  const handlePortalTargetChange = useCallback((value: string) => {
    if (!isPortalTarget(value)) {
      return;
    }

    setPortalTarget(value);
    setDraft(null);
  }, []);

  const getDraftIssueFieldLabel = useCallback(
    (field: string) => {
      const fieldLabelKey = getMeliDraftIssueFieldLabelKey(field);
      return fieldLabelKey ? t(fieldLabelKey) : field;
    },
    [t]
  );

  const getDraftIssueMessage = useCallback(
    (issue: Parameters<typeof getMeliDraftIssueMessageKey>[0]) => {
      const messageKey = getMeliDraftIssueMessageKey(issue);
      const fieldLabel = getDraftIssueFieldLabel(issue.field);
      return messageKey
        ? t(messageKey, getMeliDraftIssueMessageParams(issue, fieldLabel))
        : issue.message;
    },
    [getDraftIssueFieldLabel, t]
  );

  return {
    actions: {
      getDraftIssueFieldLabel,
      getDraftIssueMessage,
      handleEnqueuePublish,
      handlePersistedAction,
      handlePortalTargetChange,
      setDraft,
      setListingTypeId,
    },
    draft: {
      data: draft,
      groupedIssues: groupedDraftIssues,
      issueVariant: draft == null ? null : getDraftIssueVariant(draft.issues),
      loading: preparingDraft,
      params: draftParams,
      publishBlockedByIssues,
    },
    form: {
      effectiveListingTypeId,
      listingTypeId,
      listingTypeOptions,
      portalTarget:
        portalTarget ?? draft?.portalTarget ?? MeliPublicationTarget.MELI_POI,
      portalTargetOptions,
      selectedListingTypeOption,
      selectedPictureFileIds: propertyPictureFileIds,
    },
    loading: {
      action: loadingAction,
      enqueueingPause,
      enqueueingPublish,
      enqueueingUnpublish,
    },
    publication: {
      alerts: publicationAlerts,
      canPauseCurrentPublication:
        currentPublication != null &&
        canPauseMeliPublication(currentPublication),
      canUnpublishCurrentPublication:
        currentPublication != null &&
        canUnpublishMeliPublication(currentPublication),
      currentPublication,
      isSyncManagedProperty,
      paymentRequired,
      publicationEvents,
    },
    sync: {
      isSyncMode,
    },
  };
};
